/**
 * Templates. Admins see and edit every template. Managers see the admins'
 * published MASTER templates, and their own CUSTOM templates: named copies of
 * a master that they edit and add to their projects (PLAN.md 3.15).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import type { Template } from '@saledeed/shared';
import { api } from '../api/client';
import type { Label, TemplateKind, TemplateSummary } from '../api/types';
import { useCan, useUser } from '../auth/session';
import { Badge, Button, ErrorBox, Field, formatDateTime, Modal, Spinner, TableCard, TextInput } from '../components/ui';
import { ImportDialog } from './ImportDialog';
import { ReviewCountBadge } from '../reviews/ReviewTag';
import { TemplateFileDialog } from './TemplateFileDialog';

const BLANK: Template = {
  schemaVersion: 1,
  regType: 'sale',
  natureOfDocument: 'Sale Deed',
  title: 'Untitled template',
  fields: [],
  blocks: [{ type: 'heading', id: 'b1', level: 1, sentence: { id: 's1', runs: [{ kind: 'text', text: 'SALE DEED' }] } }],
};

/** Name a new custom template made from a master. */
function CopyDialog({ master, onClose }: { master: TemplateSummary; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [title, setTitle] = useState(`${master.title} - `);
  const create = useMutation({
    mutationFn: () => api<{ template: TemplateSummary }>('POST', `/api/templates/${master.id}/customise`, { body: { title: title.trim() } }),
    onSuccess: r => {
      void qc.invalidateQueries({ queryKey: ['templates'] });
      onClose();
      void navigate({ to: '/templates/$id', params: { id: r.template.id } });
    },
  });
  return (
    <Modal open title="Make my own copy" onClose={onClose}>
      <form className="flex flex-col gap-3" onSubmit={e => { e.preventDefault(); if (title.trim()) create.mutate(); }}>
        <p className="text-sm text-slate-700">
          This makes a new template of your own from <b>{master.title}</b> (v{master.currentVersion}).
          You can change its wording and fix values for a client, then add it to any of your projects.
          The admin's master template is not changed, and you can make as many copies as you need.
        </p>
        <Field label="Name of your template" hint="e.g. Sale Deed - ABC Builders">
          {id => <TextInput id={id} value={title} onChange={e => setTitle(e.target.value)} maxLength={200} required autoFocus />}
        </Field>
        <ErrorBox error={create.error} />
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button tone="primary" type="submit" disabled={!title.trim() || create.isPending}>{create.isPending ? 'Making…' : 'Make copy and open it'}</Button>
        </div>
      </form>
    </Modal>
  );
}

export function TemplatesPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { actor } = useUser();
  const canAuthor = useCan('template.author');
  const canCustomise = useCan('template.customize');
  const canManageLabels = useCan('label.manage');
  const [importing, setImporting] = useState(false);
  const [importingFile, setImportingFile] = useState(false);
  const [copying, setCopying] = useState<TemplateSummary | null>(null);
  const [labelFilter, setLabelFilter] = useState('');
  const [natureFilter, setNatureFilter] = useState('');
  const [kindFilter, setKindFilter] = useState<'' | TemplateKind>('');
  const [search, setSearch] = useState('');
  const [newLabel, setNewLabel] = useState('');

  const list = useQuery({ queryKey: ['templates'], queryFn: () => api<{ templates: TemplateSummary[] }>('GET', '/api/templates') });
  const labels = useQuery({ queryKey: ['labels'], queryFn: () => api<{ labels: Label[] }>('GET', '/api/labels') });

  const createBlank = useMutation({
    mutationFn: () => api<{ template: TemplateSummary }>('POST', '/api/templates', { body: { template: BLANK } }),
    onSuccess: r => { void qc.invalidateQueries({ queryKey: ['templates'] }); void navigate({ to: '/templates/$id', params: { id: r.template.id } }); },
  });
  const createLabel = useMutation({
    mutationFn: (name: string) => api('POST', '/api/labels', { body: { name } }),
    onSuccess: () => { setNewLabel(''); void qc.invalidateQueries({ queryKey: ['labels'] }); },
  });

  const all = list.data?.templates ?? [];
  const natures = [...new Set(all.map(t => t.natureOfDocument))].sort();
  const rows = all.filter(t =>
    (!kindFilter || t.kind === kindFilter) &&
    (!natureFilter || t.natureOfDocument === natureFilter) &&
    (!labelFilter || t.labels.some(l => l.id === labelFilter)) &&
    (!search.trim() || `${t.title} ${t.templateCode} ${t.natureOfDocument}`.toLowerCase().includes(search.trim().toLowerCase())));
  const isManager = actor === 'manager';

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold">Templates</h1>
        {canAuthor && (
          <div className="flex gap-2">
            <Button onClick={() => setImporting(true)}>Import .docx</Button>
            <Button onClick={() => setImportingFile(true)} title="A template saved as a file, or the ready-made sample">Import template file (.json)</Button>
            <Button tone="primary" onClick={() => createBlank.mutate()} disabled={createBlank.isPending}>New template</Button>
          </div>
        )}
      </div>
      {isManager && (
        <p className="rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-950">
          <b>Master</b> templates are made by the admin. Press <b>Make my own copy</b> on one to get your own
          <b> custom</b> template with its own name: change its wording, fix values for a client, then add it to
          your projects. One master can be copied as many times as you like.
        </p>
      )}
      <ErrorBox error={createBlank.error} />

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-sm">Search
          <TextInput value={search} onChange={e => setSearch(e.target.value)} placeholder="Title or code" />
        </label>
        <label className="flex flex-col text-sm">Kind
          <select className="rounded border border-slate-300 px-2 py-1.5 text-sm" value={kindFilter} onChange={e => setKindFilter(e.target.value as '' | TemplateKind)}>
            <option value="">Master and custom</option>
            <option value="master">Master (admin)</option>
            <option value="custom">{isManager ? 'My custom templates' : 'Custom (managers)'}</option>
          </select>
        </label>
        <label className="flex flex-col text-sm">Document type
          <select className="rounded border border-slate-300 px-2 py-1.5 text-sm" value={natureFilter} onChange={e => setNatureFilter(e.target.value)}>
            <option value="">All types</option>
            {natures.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-sm">Label
          <select className="rounded border border-slate-300 px-2 py-1.5 text-sm" value={labelFilter} onChange={e => setLabelFilter(e.target.value)}>
            <option value="">All labels</option>
            {labels.data?.labels.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </label>
        {canManageLabels && (
          <form className="flex items-end gap-2" onSubmit={e => { e.preventDefault(); if (newLabel.trim()) createLabel.mutate(newLabel.trim()); }}>
            <label className="flex flex-col text-sm">New label
              <TextInput value={newLabel} onChange={e => setNewLabel(e.target.value)} maxLength={60} placeholder="e.g. Guntur" />
            </label>
            <Button type="submit" disabled={!newLabel.trim() || createLabel.isPending}>Add label</Button>
          </form>
        )}
      </div>
      <ErrorBox error={createLabel.error} />

      {list.isPending ? <Spinner /> : list.isError ? <ErrorBox error={list.error} /> : (
        <TableCard>
          <caption className="sr-only">Templates</caption>
          <thead>
            <tr className="border-b border-slate-300 text-left">
              <th scope="col" className="py-2">Title</th><th scope="col">Kind</th><th scope="col">Code</th><th scope="col">Document type</th>
              <th scope="col">Status</th><th scope="col">Labels</th><th scope="col">Updated</th>
              {canCustomise && <th scope="col"><span className="sr-only">Actions</span></th>}
            </tr>
          </thead>
          <tbody>
            {rows.map(t => (
              <tr key={t.id} className="border-b border-slate-200">
                <td className="py-2">
                  {t.canEdit ? <Link to="/templates/$id" params={{ id: t.id }} className="font-medium text-blue-800 underline">{t.title}</Link> : <span className="font-medium">{t.title}</span>}
                  {' '}<ReviewCountBadge templateCode={t.templateCode} />
                  {t.kind === 'custom' && t.base && <span className="block text-xs text-slate-500">made from "{t.base.title}" v{t.base.version}</span>}
                </td>
                <td>
                  {t.kind === 'master' ? <Badge tone="slate">Master</Badge> : <Badge tone="blue">Custom</Badge>}
                  {t.kind === 'custom' && !isManager && <span className="block text-xs text-slate-500">{t.owner.displayName}</span>}
                </td>
                <td><code className="text-xs">{t.templateCode}</code></td>
                <td>{t.natureOfDocument}</td>
                <td>
                  {t.currentVersion ? <Badge tone="green">v{t.currentVersion}</Badge> : <Badge tone="amber">draft</Badge>}
                  {t.unpublishedChanges && <span className="block text-xs text-amber-800" title="Saved changes that executives do not get until you publish">changes not published</span>}
                </td>
                <td>{t.labels.map(l => <Badge key={l.id}>{l.name}</Badge>)}</td>
                <td className="text-xs text-slate-600">{formatDateTime(t.updatedAt)}</td>
                {canCustomise && (
                  <td className="whitespace-nowrap text-right">
                    {t.kind === 'master' && t.currentVersion !== null && t.status !== 'archived' && (
                      <Button tone="ghost" onClick={() => setCopying(t)} aria-label={`Make my own copy of ${t.title}`}
                        title="Make your own named template from this master">Make my own copy</Button>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={8} className="py-6 text-center text-slate-500">
                No templates{labelFilter || natureFilter || kindFilter || search ? ' match' : ' yet'}.
              </td></tr>
            )}
          </tbody>
        </TableCard>
      )}

      {canAuthor && (
        <ImportDialog
          open={importing} onClose={() => setImporting(false)}
          onDone={created => {
            setImporting(false);
            if (created.length === 1) void navigate({ to: '/templates/$id', params: { id: created[0]!.id } });
          }}
        />
      )}
      {canAuthor && (
        <TemplateFileDialog
          open={importingFile} onClose={() => setImportingFile(false)}
          onOpen={id => { setImportingFile(false); void navigate({ to: '/templates/$id', params: { id } }); }}
        />
      )}
      {copying && <CopyDialog master={copying} onClose={() => setCopying(null)} />}
    </div>
  );
}
